import { useState } from "react";
import { CheckCircle2, RotateCcw, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Money } from "@/components/common";
import { FileDrop, MappingEditor, StatusPill, SummaryGrid } from "@/components/FileImport";
import ConfirmDialog from "@/components/ConfirmDialog";
import UploadHistory from "@/components/UploadHistory";
import { isoToDmy } from "@/components/DatePicker";
import { api, errMsg, newKey } from "@/lib/api";
import { inr, currentMonth, monthLabel } from "@/lib/format";
import { toast } from "sonner";

const FIELDS = [["code", "Employee Code", true], ["name", "Employee Name"], ["amount", "Deduction Amount", true], ["date", "Deduction Date"], ["reference", "Reference / Payroll Month"], ["remarks", "Remarks"]];

function PreviewTable({ rows, confirm, toggle }) {
  return (
    <div className="bg-white rounded-xl border overflow-x-auto max-h-[480px]" data-testid="bulk-deduction-preview-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 sticky top-0">
          <tr><th className="px-3 py-2 text-left">Row</th><th className="px-3 py-2 text-left">Employee</th><th className="px-3 py-2 text-right">Current</th><th className="px-3 py-2 text-right">Deduct</th><th className="px-3 py-2 text-right">Final</th><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Reference</th><th className="px-3 py-2 text-left">Status</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.row} className="border-t border-slate-100 align-top">
              <td className="px-3 py-2 text-slate-400">{r.row}</td>
              <td className="px-3 py-2"><span className="money text-blue-800">{r.code || "—"}</span> {r.name || r.file_name}</td>
              <td className="px-3 py-2 text-right">{r.current_balance != null ? <Money value={r.current_balance} /> : "—"}</td>
              <td className="px-3 py-2 text-right text-emerald-700">{r.amount != null ? <Money value={r.amount} /> : "—"}</td>
              <td className="px-3 py-2 text-right font-semibold">{r.final_balance != null ? <Money value={r.final_balance} /> : "—"}</td>
              <td className="px-3 py-2 money">{isoToDmy(r.date) || "—"}</td>
              <td className="px-3 py-2 text-xs">{r.reference || "—"}</td>
              <td className="px-3 py-2 text-xs space-y-1">
                <StatusPill status={r.status} />
                {r.error && r.status !== "possible_duplicate" && <div className="text-red-700">{r.error}</div>}
                {r.status === "possible_duplicate" && (
                  <label className="flex items-start gap-2 cursor-pointer text-amber-800">
                    <Checkbox checked={confirm.has(r.code)} onCheckedChange={(v) => toggle(r.code, v)} data-testid={`confirm-bulk-deduction-${r.code}`} />
                    <span>{r.error}. Tick to record anyway.</span>
                  </label>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function BulkDeductions({ disabled }) {
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [reference, setReference] = useState(`Payroll ${monthLabel(currentMonth())}`);
  const [confirm, setConfirm] = useState(new Set());
  const [batch, setBatch] = useState(newKey());
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(false);
  const [summary, setSummary] = useState(null);

  const parse = async (f, mapping) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f); fd.append("kind", "deductions"); fd.append("default_reference", reference);
    if (mapping) fd.append("mapping", JSON.stringify(mapping));
    try {
      const { data } = await api.post("/import/parse", fd);
      setFile(f); setParsed(data); setConfirm(new Set()); setSummary(null); setBatch(newKey());
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/import/deductions/commit", { rows: parsed.rows, confirm_codes: [...confirm], filename: parsed.filename, batch_key: batch, default_reference: reference });
      setSummary(data); setAsk(false); toast.success(`${data.recorded} deductions recorded (${inr(data.amount)})`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const toggle = (c, v) => { const s = new Set(confirm); v ? s.add(c) : s.delete(c); setConfirm(s); };
  const reset = () => { setParsed(null); setSummary(null); };

  const c = parsed?.counts || {};
  const ready = parsed?.rows.filter((r) => r.status === "ok" || (r.status === "possible_duplicate" && confirm.has(r.code))) || [];
  const total = ready.reduce((a, r) => a + Math.round(r.amount * 100), 0) / 100;

  if (summary) return (
    <div className="space-y-4 fade-up" data-testid="bulk-deduction-summary">
      <div className="flex items-center gap-2 text-emerald-700 font-semibold"><CheckCircle2 className="h-5 w-5" /> Bulk deductions recorded from {parsed.filename}</div>
      <SummaryGrid testid="bulk-deduction-summary-grid" items={[
        ["total", "Rows in file", summary.total], ["recorded", "Recorded", summary.recorded, "text-emerald-700"], ["amount", "Total deducted", inr(summary.amount), "text-emerald-700 text-lg"],
        ["skipped", "Skipped", summary.skipped + summary.already_recorded], ["duplicates", "Duplicates", summary.duplicates, "text-orange-700"], ["invalid", "Invalid", summary.invalid, "text-red-700"],
      ]} />
      <Button variant="outline" onClick={reset} data-testid="bulk-deduction-another"><RotateCcw className="h-4 w-4" /> Upload another file</Button>
      <UploadHistory refreshKey={summary.batch_id} />
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border p-4 max-w-xl">
        <Label>Default reference / payroll month</Label>
        <Input className="mt-1.5" value={reference} onChange={(e) => setReference(e.target.value)} disabled={disabled || !!parsed} data-testid="bulk-deduction-reference" />
        <p className="text-xs text-slate-500 mt-1">Used for rows where the file has no reference. Blank dates use today.</p>
      </div>
      {!parsed ? (
        <>
          <FileDrop onFile={(f) => !disabled && parse(f)} busy={busy} testid="bulk-deduction" template="deductions" />
          <UploadHistory />
        </>
      ) : (
        <>
          <div className="flex justify-between items-center text-sm text-slate-600"><span>File: <b>{parsed.filename}</b> · {parsed.total} rows</span><Button variant="outline" size="sm" onClick={reset} data-testid="bulk-deduction-reset"><RotateCcw className="h-4 w-4" /> New file</Button></div>
          <MappingEditor key={JSON.stringify(parsed.mapping)} headers={parsed.headers} mapping={parsed.mapping} fields={FIELDS} busy={busy} testid="bulk-deduction" onApply={(m) => parse(file, m)} />
          {parsed.missing_fields.length > 0 ? (
            <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"><AlertTriangle className="h-4 w-4" /> Could not detect: {parsed.missing_fields.join(", ")}. Map the columns and click Apply.</div>
          ) : (
            <>
              <PreviewTable rows={parsed.rows} confirm={confirm} toggle={toggle} />
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white rounded-xl border p-4">
                <div className="text-sm text-slate-600" data-testid="bulk-deduction-ready-text">{ready.length} deductions ready · total <b className="money">{inr(total)}</b> · {parsed.rows.length - ready.length} rows will be skipped</div>
                <Button className="bg-blue-800 hover:bg-blue-900" disabled={disabled || busy || ready.length === 0} onClick={() => setAsk(true)} data-testid="bulk-deduction-commit-btn">Record {ready.length} deductions</Button>
              </div>
            </>
          )}
        </>
      )}
      <ConfirmDialog open={ask} onOpenChange={setAsk} busy={busy} onConfirm={commit} confirmText="Yes, record all" testid="bulk-deduction-confirm-dialog"
        title="Confirm bulk advance deductions" description={`Record ${ready.length} deductions totalling ${inr(total)}. Each one is added to the employee's ledger and the audit trail.`} />
    </div>
  );
}
